import LaunchIcon from '@material-ui/icons/Launch'
import React from 'react'
import styled, { css } from 'styled-components'

import { Card, Grid } from '../../../components/Base/Base'
import { FlexBox } from '../../../components/Base/FlexBox'
import { Box } from '../../../components/Box/Box'
import { Button } from '../../../components/Button/Button'
import { BlueGreyEditButton, EditButton } from '../../../components/Button/EditButton'
import { useUserProvider } from '../../../components/Context/UserContext'
import { CopyToClipBoard } from '../../../components/CopyToClipBoard/CopyToClipBoard'
import { Pill } from '../../../components/Pill/Pill'
import { Text } from '../../../components/Text/Text'
import { isConsole } from '../../../config/config'
import { formatMobile } from '../../../helpers/formatters'
import { Handler, HandlerOf } from '../../../helpers/typeHelper'
import { SlimTeam } from '../../../models/team'
import { Truck } from '../../../models/truck'
import { DriverStatus, User, UserRoles } from '../../../models/user'
import { breakpoints } from '../../../theme/breakpoints'
import { darkGray, error, errorD1, midGray, primaryBlack, royalBlue } from '../../../theme/colors'
import { header4 } from '../../../theme/typography'
import { ProfileHeaderDriverStatus } from './ProfileHeaderDriverStatus'
import { ProfilePageHOS } from './ProfilePageHOS'
import { TeamEntitlements } from './TeamEntitlements'

interface ProfileHeaderProps {
  user: User
  team: SlimTeam | null
  assignedTruck?: Truck
  teamOpener?: (sidebarOpener: React.ReactNode) => React.ReactNode
  onUserRoleChange?: HandlerOf<User>
  onProgramEnrollment?: HandlerOf<User>
  onTeamChange?: Handler
  isInsideSidebar?: boolean
  renderDriverTransition?: () => React.ReactNode
  renderOpenNotesCard: () => React.ReactNode
}

export function ProfileHeader({
  user,
  assignedTruck,
  team,
  teamOpener,
  onUserRoleChange,
  onProgramEnrollment,
  onTeamChange,
  renderDriverTransition,
  renderOpenNotesCard,
  isInsideSidebar,
}: ProfileHeaderProps) {
  const { subscription, loggedInUser } = useUserProvider()
  const showHOS =
    !isInsideSidebar &&
    subscription?.isPremium &&
    user.driver.status &&
    ![DriverStatus.APPLYING, DriverStatus.ELIGIBLE, DriverStatus.ONBOARDING].includes(user.driver.status)
  // In the web app notes should be hidden for non-admin users and for vc/premium users, only show for flex starter admins
  const shouldRenderNotesCard =
    isConsole() || loggedInUser?.isStaff || (subscription?.isFlexStarter && loggedInUser?.isTeamAdmin)

  const mobile = formatMobile(user.mobile!) || '-'

  function renderTeamName() {
    if (!isConsole()) {
      return null
    }

    if (!team) {
      return (
        <Pill background={error} color={errorD1} mb={2}>
          <FlexBox alignItems="center" gap="10px">
            <span>No team</span>

            <BlueGreyEditButton
              ariaLabel="edit"
              onClick={(e) => {
                e.stopPropagation()
                onTeamChange?.()
              }}
            />
          </FlexBox>
        </Pill>
      )
    }

    if (isInsideSidebar) {
      return (
        <FlexBox alignItems="center" gap="16px" mb={2}>
          {teamOpener ? (
            teamOpener(<ProfileHeaderTeamName team={team} isInsideSidebar />)
          ) : (
            <ProfileHeaderTeamName team={team} href={`/teams/${team?.id}/show`} isInsideSidebar />
          )}
          <TeamEntitlements team={team} isInsideSidebar={isInsideSidebar} />
        </FlexBox>
      )
    }

    if (teamOpener) {
      return teamOpener(<ProfileHeaderTeamName team={team} onEdit={onTeamChange} />)
    }

    return <ProfileHeaderTeamName team={team} href={`/teams/${team?.id}/show`} onEdit={onTeamChange} />
  }

  return (
    <StyledGrid
      columns={`minmax(180px, 1fr) 1fr`}
      gap="24px"
      alignItems="flex-start"
      $isInsideSidebar={isInsideSidebar}
    >
      <div>
        {renderTeamName()}

        <Text variant="header2" fontColor={primaryBlack}>
          {user.firstName} {user.lastName}
        </Text>

        <Box mt={1} />

        {team && (
          <FlexBox gap="5px" alignItems="center">
            <Text variant="uppercaseLabel3" fontColor={midGray} data-testid="userRole">
              {getUserRole(user, isInsideSidebar)}
            </Text>
            {!isInsideSidebar && onUserRoleChange && (
              <StyledEditButton
                ariaLabel="Edit permissions"
                onClick={() => onUserRoleChange(user)}
                padding="5px"
              />
            )}
          </FlexBox>
        )}

        {isInsideSidebar && (
          <>
            <Box mt={2} />
            <ProfileHeaderDriverStatus user={user} />
          </>
        )}

        {isInsideSidebar && (
          <>
            <Box mt={3} />
            <ProfileHeaderContact user={user} mobile={mobile} />
          </>
        )}

        {!isInsideSidebar && isConsole() && team && (
          <FlexBox gap="10px" alignItems="center" mt={2} mb={2}>
            <ProfileHeaderDriverStatus user={user} />
            {renderDriverTransition?.()}
          </FlexBox>
        )}

        {!isInsideSidebar && isConsole() && team && (
          <FlexBox gap="10px" alignItems="center">
            {user.defaultTeamMembership?.hasDispatchServiceFreeFeature && (
              <>
                <Text variant="header4" fontColor={primaryBlack} data-testid="userProgramEnrollment">
                  Dispatch Service Free
                </Text>
                <Text variant="label4" fontColor={midGray} data-testid="userRole">
                  {user.defaultTeamMembership?.hasTruckLeaseFeature ? '|' : null}
                </Text>
              </>
            )}
            {user.defaultTeamMembership?.hasDispatchServiceFeature && (
              <>
                <Text variant="header4" fontColor={primaryBlack} data-testid="userProgramEnrollment">
                  Dispatch Service
                </Text>
                <Text variant="label4" fontColor={midGray} data-testid="userRole">
                  {user.defaultTeamMembership?.hasTruckLeaseFeature ? '|' : null}
                </Text>
              </>
            )}
            {user.defaultTeamMembership?.hasTruckLeaseFeature && (
              <Text variant="header4" fontColor={primaryBlack} data-testid="userProgramEnrollment">
                Truck Lease
              </Text>
            )}
            {!isInsideSidebar && onProgramEnrollment && (
              <>
                {(user.defaultTeamMembership?.hasDispatchServiceFeature ||
                  user.defaultTeamMembership?.hasDispatchServiceFreeFeature ||
                  user.defaultTeamMembership?.hasTruckLeaseFeature) && (
                  <Text variant="label4" fontColor={midGray} data-testid="userProgramEnrollment">
                    {user.defaultTeamMembership?.hasDispatchServiceFeature ||
                    user.defaultTeamMembership?.hasTruckLeaseFeature ||
                    user.defaultTeamMembership?.hasDispatchServiceFreeFeature
                      ? '|'
                      : null}
                  </Text>
                )}
                <Button variant="text" size="small" onClick={() => onProgramEnrollment(user)}>
                  Manage Programs
                </Button>
              </>
            )}
          </FlexBox>
        )}

        {isInsideSidebar && (
          <>
            <Box mt={1} />
            <FlexBox gap="8px">
              <Text variant="label2" fontColor={darkGray}>
                Vehicle ID (Motive):
              </Text>
              {assignedTruck?.eldVehicles?.[0]?.providerName ? (
                <CopyToClipBoard
                  text={assignedTruck?.eldVehicles?.[0]?.providerName}
                  textProps={{ variant: 'label2' }}
                  endIcon={
                    <Button
                      variant="text"
                      icon={<LaunchIcon fontSize="small" htmlColor={royalBlue} />}
                      href={`/teams/${user.team}/show/equipment`}
                      mt={-0.25}
                    />
                  }
                >
                  <Text variant="label2" fontColor={darkGray}>
                    {assignedTruck?.eldVehicles?.[0]?.providerName}
                  </Text>
                </CopyToClipBoard>
              ) : (
                <Text variant="label2" fontColor={darkGray}>
                  Not provided
                </Text>
              )}
            </FlexBox>
          </>
        )}
      </div>

      <div>
        <StyledCardsGrid columns="1fr" gap="24px" alignItems="center" justifyItems="end">
          {showHOS && <ProfilePageHOS user={user} />}
          {shouldRenderNotesCard && renderOpenNotesCard()}
        </StyledCardsGrid>
      </div>
    </StyledGrid>
  )
}

const ProfileHeaderTeamName = ({
  team,
  href,
  onEdit,
  isInsideSidebar,
}: {
  team: SlimTeam
  href?: string
  onEdit?: Handler
  isInsideSidebar?: boolean
}) => (
  <FlexBox alignItems="center">
    <StyledButton variant="text" $isInsideSidebar={isInsideSidebar} href={href}>
      {team.name} {!isInsideSidebar && <TeamEntitlements team={team} />}
    </StyledButton>
    {!isInsideSidebar && onEdit && (
      <BlueGreyEditButton
        ariaLabel="edit"
        onClick={(e) => {
          e.stopPropagation()
          onEdit()
        }}
      />
    )}
  </FlexBox>
)

// TODO: @public-console-sidebar Componentize the copy to clipboard functionality with text
const ProfileHeaderContact = ({ user, mobile }: { user: User; mobile: string }) => (
  <FlexBox isColumn alignItems="flex-start" gap="8px">
    <CopyToClipBoard text={user.email!} textProps={{ variant: 'label2' }}>
      <Text variant="label2" fontColor={darkGray}>
        {user.email}
      </Text>
    </CopyToClipBoard>
    {user.mobile ? (
      <CopyToClipBoard text={mobile} textProps={{ variant: 'label2' }}>
        <Text variant="label2" fontColor={darkGray}>
          {mobile}
        </Text>
      </CopyToClipBoard>
    ) : (
      <Text variant="label2" fontColor={darkGray}>
        {mobile}
      </Text>
    )}
  </FlexBox>
)

const StyledEditButton = styled(EditButton)`
  margin-top: -3px;

  svg {
    width: 15px;
    height: 15px;
  }
`

const getUserRole = (user: User, isSidebar?: boolean) => {
  let roles = user.roles

  if (roles) {
    if (roles.includes(UserRoles.BUSINESS_OWNER) && roles.includes(UserRoles.DRIVER)) {
      return isSidebar ? 'Admin • Driver • Owner' : 'Business Owner/Driver'
    }
    if (roles.includes(UserRoles.BUSINESS_OWNER)) {
      return 'Business Owner'
    }
    if (roles.includes(UserRoles.TEAM_ADMIN) && roles.includes(UserRoles.DRIVER)) {
      return 'Admin • Driver'
    }
    if (roles.includes(UserRoles.TEAM_ADMIN)) {
      return 'Admin'
    }
    if (roles.includes(UserRoles.DRIVER)) {
      return 'Driver'
    }
  }
  return ''
}

const StyledButton = styled(Button)<{ $isInsideSidebar?: boolean }>`
  ${({ $isInsideSidebar }) =>
    $isInsideSidebar &&
    css`
      ${header4};
      color: ${royalBlue};
    `}

  justify-content: flex-start;
  text-align: left;
  border-radius: 0;
  display: block;
  margin: 0 0 2px 0;

  > div {
    line-height: 1.3;
  }
`

export const ProfileHeaderCard = styled(Card).attrs({
  hasSubtleShadow: true,
  smallPadding: true,
  align: 'left',
})`
  width: 324px;
  max-width: 100%;
  align-self: stretch;

  @media ${breakpoints.sm} {
    width: 100%;
  }
`

const StyledCardsGrid = styled(Grid)`
  @media ${breakpoints.sm} {
    grid-template-columns: ${isConsole() ? '1fr' : '1fr 1fr'};
    gap: 16px;
    justify-items: normal;
  }

  @media ${breakpoints.xs} {
    grid-template-columns: 1fr;
  }
`

const StyledGrid = styled(Grid)<{ $isInsideSidebar?: boolean }>`
  grid-template-columns: 1fr;
  ${(p) => !p.$isInsideSidebar && `grid-template-columns: 1fr 1fr;`}

  @media ${breakpoints.sm} {
    grid-template-columns: 1fr;
  }
`
